import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaStar, FaShoppingCart } from 'react-icons/fa';
import { Scan } from 'lucide-react';
import { useBranch } from '~/contexts/BranchContext';
import { getItemName, getItemDescription } from '~/utils/menuItemHelper';
import type { MenuItem } from '~/types/new_types';
import OptimizedImage from '../imageHandler/OptimizedImage';

// Animation variants - defined at module level to prevent recreation on each render
const MENU_ITEM_CARD_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.15 },
  },
};

interface SharedMenuItemCardProps {
  item: MenuItem;
  /** Callback when zoom button is clicked */
  onZoom: (item: MenuItem) => void;
  /** Callback when add button is clicked */
  onAdd?: (item: MenuItem) => void;
}

/**
 * Shared menu item card component - List style card with horizontal layout.
 * Displays item image, name, description, price, rating, and dietary info.
 * Used by both one-page layout and default layout for consistent appearance.
 */
function SharedMenuItemCardComponent({ item, onZoom, onAdd }: SharedMenuItemCardProps) {
  const { branchData, theme, getConvertedPrice, setSelectedItem } = useBranch();
  const { t, i18n } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const isRTL = i18n.language === 'ar';

  const displayPrice = getConvertedPrice(item.price);
  const displayDiscountedPrice = item.discounted_price
    ? getConvertedPrice(item.discounted_price)
    : null;

  // Normalize 0-1 backend rating to a 0-5 display scale with partial star support
  const rawRating = Number(item.item_rate ?? 0);
  const normalizedRating = Number.isFinite(rawRating)
    ? Math.max(0, Math.min(5, rawRating * 5))
    : 0;
  const roundedRating = Math.round(normalizedRating * 2) / 2;
  const hasRating = roundedRating > 0;
  const formattedRating = hasRating
    ? isRTL
      ? roundedRating.toLocaleString('ar-EG', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
      : roundedRating.toFixed(1)
    : null;

  return (
    <motion.div
      variants={MENU_ITEM_CARD_VARIANTS}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ y: -2 }}
      className="cursor-pointer group overflow-hidden active:scale-[0.98] transition-transform"
      onClick={() => setSelectedItem(item)}
      style={{
        backgroundColor: theme?.cardColor,
        border:
          theme?.cardStyle === 'bordered'
            ? `1px solid ${theme?.borderColor}`
            : 'none',
        boxShadow: isHovered
          ? `0 8px 20px ${theme?.primaryColor}30`
          : theme?.cardStyle === 'shadow'
            ? `0 4px 8px ${theme?.primaryColor}20`
            : '',
        borderRadius: theme?.borderRadius,
      }}
    >
      <div className="flex h-[145px] sm:h-[140px] md:h-[160px]">
        {/* Image Section */}
        <div className="relative h-full aspect-square flex-shrink-0 overflow-hidden rounded-l-lg">
          <OptimizedImage
            loading="lazy"
            src={
              item.image_url ??
              branchData?.branch?.default_item_image ??
              '/images/no-image-available.jpg'
            }
            alt={getItemName(item, i18n.language)}
            className="w-full h-full object-cover pointer-events-none select-none"
            animate={{ scale: isHovered ? 1.05 : 1 }}
            transition={{ duration: 0.2 }}
          />

          {/* Zoom Button */}
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              onZoom(item);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="absolute bottom-2 end-2 sm:bottom-3 sm:end-3 z-10 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center backdrop-blur-sm transition-all hover:scale-110"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: theme?.primaryColor,
              borderRadius: '6px',
            }}
            title={t('menu.zoomImage', { defaultValue: 'Zoom image' })}
          >
            <Scan size={12} className="sm:hidden" />
            <Scan size={14} className="hidden sm:block" />
          </motion.button>

          {/* Popular badge */}
          {item.is_featured && (
            <div
              className={`absolute top-2 ${isRTL ? 'end-2' : 'start-2'} sm:top-3 ${isRTL ? 'sm:end-3' : 'sm:start-3'}`}
            >
              <div
                className="px-2 py-1 sm:px-2.5 sm:py-1 bg-yellow-50 text-yellow-700 text-xs sm:text-sm font-semibold flex items-center gap-1"
                style={{ borderRadius: theme?.borderRadius }}
              >
                <FaStar className="text-yellow-500" size={10} />
                <span className="hidden xs:inline">{t('menu.popular')}</span>
              </div>
            </div>
          )}

          {/* Unavailable overlay */}
          {!item.is_available && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
              <motion.img
                loading="lazy"
                src={`/images/not-availbale-stampel-${i18n.language}.png`}
                className="h-1/2 xs:h-2/3 -rotate-10 pointer-events-none select-none"
                animate={{ scale: isHovered ? 1.05 : 1 }}
                transition={{ duration: 0.4 }}
              />
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="relative flex-1 p-3 xs:p-4 sm:p-5 md:p-6 flex min-w-0">
          {!item.is_available && (
            <div className="absolute inset-0 bg-black/20 rounded-r-lg" />
          )}

          {/* Left side: Title, Rating, Description, Dietary Icons */}
          <div className="flex-1 min-w-0 flex flex-col justify-between">
            <div>
              <h3
                className={`block min-w-0 text-base xs:text-lg sm:text-xl md:${theme?.itemFontSize} ${theme?.boldTitles ? 'font-bold' : 'font-semibold'} leading-tight truncate mb-1`}
                style={{ color: theme?.textColor }}
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                title={getItemName(item, i18n.language)}
              >
                {getItemName(item, i18n.language)}
              </h3>

              {/* Star Rating */}
              {hasRating && (
                <StarRating
                  rating={roundedRating}
                  formattedRating={formattedRating}
                  isRTL={isRTL}
                  textColor={theme?.textColor}
                />
              )}

              {/* Description */}
              {getItemDescription(item, i18n.language) && (
                <p
                  className={`text-sm xs:text-base sm:text-lg w-50 sm:w-full ${!item.is_available || hasRating
                      ? 'line-clamp-2'
                      : 'line-clamp-3'
                    } leading-relaxed`}
                  style={{ color: `${theme?.textColor}75` }}
                  dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                >
                  {getItemDescription(item, i18n.language)}
                </p>
              )}
            </div>

            {/* Mobile price position: below title/description */}
            <div className="mt-2 sm:hidden">
              <PriceDisplay
                displayPrice={displayPrice}
                displayDiscountedPrice={displayDiscountedPrice}
                theme={theme}
              />
            </div>

            {/* Bottom row with metadata */}
            <ItemMetadata item={item} theme={theme} t={t} />
          </div>

          {/* Right side: Price and Feedback button stacked */}
          <div className="flex-shrink-0 flex flex-col items-end justify-between gap-2 ms-3">
            {/* Price section */}
            <div className="hidden sm:block">
              <PriceDisplay
                displayPrice={displayPrice}
                displayDiscountedPrice={displayDiscountedPrice}
                theme={theme}
              />
            </div>

            {/* Action button */}
            <div className="flex items-center mt-auto">
              {/* Add to Cart button */}
              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  onAdd?.(item);
                }}
                className="w-8 h-8 xs:w-9 xs:h-9 rounded-full flex items-center justify-center transition-all text-white shadow-sm"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={{
                  backgroundColor: theme?.primaryColor,
                }}
                title={t('menu.addToOrder')}
              >
                <FaShoppingCart size={12} className="xs:hidden" />
                <FaShoppingCart size={14} className="hidden xs:block" />
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Sub-components for better organization
interface StarRatingProps {
  rating: number;
  formattedRating: string | null;
  isRTL: boolean;
  textColor?: string;
}

function StarRating({ rating, formattedRating, isRTL, textColor }: StarRatingProps) {
  return (
    <div
      className="flex items-center gap-0.5 mb-1.5"
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      {Array.from({ length: 5 }).map((_, index) => {
        const fill = Math.min(Math.max(rating - index, 0), 1);
        const fillStyle: React.CSSProperties = {
          width: `${fill * 100}%`,
          height: '100%',
          position: 'absolute',
          top: 0,
          overflow: 'hidden',
          ...(isRTL ? { right: 0 } : { left: 0 }),
        };

        return (
          <span
            key={index}
            className="relative inline-flex"
            style={{ width: 12, height: 12 }}
          >
            <FaStar size={12} className="text-gray-300" aria-hidden="true" />
            {fill > 0 && (
              <span style={fillStyle} aria-hidden="true">
                <FaStar size={12} className="text-yellow-500" />
              </span>
            )}
          </span>
        );
      })}
      {formattedRating && (
        <span
          className={`text-xs ${isRTL ? 'mr-1' : 'ml-1'}`}
          style={{ color: `${textColor}60` }}
        >
          ({formattedRating})
        </span>
      )}
    </div>
  );
}

interface ItemMetadataProps {
  item: MenuItem;
  theme: { accentColor?: string; textColor?: string } | null;
  t: (key: string) => string;
}

function ItemMetadata({ item, theme, t }: ItemMetadataProps) {
  return (
    <div className="flex items-center gap-2 xs:gap-3 pt-2 xs:pt-3">
      {/* Spice level indicator */}
      {item.spice_level && item.spice_level !== 'none' && (
        <div
          className="flex items-center gap-0.5"
          title={t(`spiceLevels.${item.spice_level}`)}
        >
          {Array.from({
            length:
              item.spice_level === 'mild'
                ? 1
                : item.spice_level === 'medium'
                  ? 2
                  : item.spice_level === 'hot'
                    ? 3
                    : 4,
          }).map((_, i) => (
            <span key={i} className="text-red-500 text-xs xs:text-sm">
              🌶️
            </span>
          ))}
        </div>
      )}

      {/* Preparation time */}
      {item.preparation_time && (
        <div className="flex items-center gap-1 text-xs xs:text-sm text-gray-500 font-medium">
          <span className="text-sm">⏱️</span>
          <span className="hidden xs:inline">
            {item.preparation_time} {t('menu.minutes')}
          </span>
          <span className="xs:hidden">{item.preparation_time}m</span>
        </div>
      )}

      {/* Unavailable indicator */}
      {!item.is_available && (
        <span className="text-xs xs:text-sm text-red-500 font-semibold">
          {t('menu.unavailable')}
        </span>
      )}
    </div>
  );
}

interface PriceDisplayProps {
  displayPrice: string;
  displayDiscountedPrice: string | null;
  theme: { primaryColor?: string; textColor?: string } | null;
}

function PriceDisplay({ displayPrice, displayDiscountedPrice, theme }: PriceDisplayProps) {
  if (displayDiscountedPrice) {
    return (
      <div className="text-end">
        <div className="flex flex-col items-end">
          <span
            className="text-xs xs:text-sm line-through"
            style={{ color: `${theme?.textColor}60` }}
          >
            {displayPrice}
          </span>
          <span
            className="font-bold text-base xs:text-lg sm:text-xl"
            style={{ color: theme?.primaryColor }}
          >
            {displayDiscountedPrice}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="text-start">
      <span
        className="font-bold text-base xs:text-lg sm:text-xl"
        style={{ color: theme?.primaryColor }}
      >
        {displayPrice}
      </span>
    </div>
  );
}

SharedMenuItemCardComponent.displayName = 'SharedMenuItemCard';

export const SharedMenuItemCard = React.memo(SharedMenuItemCardComponent);
export default SharedMenuItemCard;
